import React, { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import AddManufacturer from "./AddManufacturer";
import api from "./Api.js";
import { FilePond, registerPlugin } from "react-filepond";
import "filepond/dist/filepond.min.css";
import { ToggleButton, ToggleButtonGroup, Form } from "react-bootstrap";

import FilePondPluginImageExifOrientation from "filepond-plugin-image-exif-orientation";
import FilePondPluginImagePreview from "filepond-plugin-image-preview";
import "filepond-plugin-image-preview/dist/filepond-plugin-image-preview.css";
import FilePondPluginFileValidateType from 'filepond-plugin-file-validate-type';

function App() {
  const [manufacturers, setManufacturers] = useState([]);
  const [selectedManufacturer, setSelectedManufacturer] = useState(null);
  const [productName, setProductName] = useState("");
  const [category, setCategory] = useState("");
  const [subCategories, setSubCategories] = useState([]);
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [materials, setMaterials] = useState([]);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const [recyclableCodes, setRecyclableCodes] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [images, setImages] = useState([]);
  const [cleaningFileInput, setCleaningFileInput] = useState();
  const [sortingFileInput, setSortingFileInput] = useState();
  const [cleaningText, setCleaningText] = useState();
  const [sortingText, setSortingText] = useState();
  const [cleaningVideoURL, setCleaningVideoURL] = useState();
  const [sortingVideoURL, setSortingVideoURL] = useState();

  const [cleaningInputType, setCleaningInputType] = useState();
  const [sortingInputType, setSortingInputType] = useState();
  const [cleaningSectionToggle, setCleaningSectionToggle] = useState(false);
  const [sortingSectionToggle, setSortingSectionToggle] = useState(false);

  // Fetch data from public directory
  useEffect(() => {
    async function fetchData() {
      try {
        axios
          .get("/recyclable_codes.json")
          .then((response) => {
            console.log("Loaded recyclable codes:", response.data);
            setRecyclableCodes(response.data);
          })
          .catch((error) => {
            console.error("Error loading recyclable codes:", error);
          });

        api
          .get("/api/manufacturers")
          .then((response) => {
            setManufacturers(
              response.data.map((manu) => ({
                value: manu.ManufacturerID,
                label: manu.Name,
              }))
            );
          })
          .catch((error) => {
            console.error("Error loading manufacturers:", error);
          });
      } catch (error) {
        console.error("Failed to fetch data:", error);
      }
    }
    fetchData();
  }, []);

  // Update sub-categories when a category is selected
  useEffect(() => {
    if (category && recyclableCodes[category]) {
      const subCategoryOptions = Object.keys(recyclableCodes[category]).map(
        (subCategory) => ({
          value: subCategory,
          label: subCategory,
        })
      );
      setSubCategories(subCategoryOptions);
    } else {
      setSubCategories([]);
    }
  }, [category, recyclableCodes]);

  // Update materials when a sub-category is selected
  useEffect(() => {
    if (
      selectedSubCategory &&
      category &&
      recyclableCodes[category][selectedSubCategory]
    ) {
      const materialOptions = recyclableCodes[category][
        selectedSubCategory
      ].map((material) => ({
        value: material.code,
        label: material.name,
      }));
      setMaterials(materialOptions);
    } else {
      setMaterials([]);
    }
  }, [selectedSubCategory, category, recyclableCodes]);

  const handleCategoryChange = (selectedOption) => {
    setCategory(selectedOption.value);
    setSelectedSubCategory(null); // Reset sub-category when category changes
  };

  const handleSubCategoryChange = (selectedOption) => {
    setSelectedSubCategory(selectedOption.value);
    setSelectedMaterial(null); // Reset material when sub-category changes
  };

  const handleMaterialChange = (selectedOption) => {
    setSelectedMaterial(selectedOption);
  };

  const handleImageChange = (e) => {
    setImages([...e.target.files].slice(0, 10));
  };

  const handleCleaningChange = (val) => {
    setCleaningInputType(val);
    if (val === 1) {
      setCleaningText("");
      setCleaningVideoURL("");
    } else if (val === 2) {
      setCleaningFileInput([]);
      setCleaningVideoURL("");
    } else {
      setCleaningFileInput([]);
      setCleaningText("");
    }
  };

  const handleSortingChange = (val) => {
    setSortingInputType(val);
    if (val === 4) {
      setSortingText("");
      setSortingVideoURL("");
    } else if (val === 5) {
      setSortingFileInput([]);
      setSortingVideoURL("");
    } else {
      setSortingFileInput([]);
      setSortingText("");
    }
  }

  const handleCleaningSectionToggle = () => {
    setCleaningSectionToggle(!cleaningSectionToggle);
    setCleaningFileInput([]);
    setCleaningText("");
    setCleaningVideoURL("");
  };

  const handleSortingSectionToggle = () => {
    setSortingSectionToggle(!sortingSectionToggle);
    setSortingFileInput([]);
    setSortingText("");
    setSortingVideoURL("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedManufacturer || !productName || !selectedMaterial) {
      alert("Please fill all required fields");
      return;
    }

    const formData = new FormData();
    formData.append("manufacturerId", selectedManufacturer.value);
    formData.append("productName", productName);
    formData.append("categoryId", category);
    formData.append("subCategoryId", selectedSubCategory);
    formData.append("materialCode", selectedMaterial.value);
    images.forEach((image) => formData.append("images", image.file));

    if (cleaningSectionToggle) {
      if (cleaningInputType === 1) {
        formData.append("cleaningFile", cleaningFileInput[0].file);
      } else if (cleaningInputType === 2) {
        formData.append("cleaningText", cleaningText);
      } else if (cleaningInputType === 3) {
        formData.append("cleaningVideoURL", cleaningVideoURL);
      }
    }

    if (sortingSectionToggle) {
      if (sortingInputType === 1) {
        formData.append("sortingFile", sortingFileInput[0].file);
      } else if (sortingInputType === 2) {
        formData.append("sortingText", sortingText);
      } else if (sortingInputType === 3) {
        formData.append("sortingVideoURL", sortingVideoURL);
      }
    }

    try {
      await api.post("/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      alert("Product uploaded successfully!");
    } catch (error) {
      alert("Failed to upload product");
      console.error(error);
    }
  };

  registerPlugin(
    FilePondPluginImageExifOrientation,
    FilePondPluginImagePreview,
    FilePondPluginFileValidateType
  );

  return (
    <div className="container my-3" style={{}}>
      <div className="row">
        <div className="shadow rounded p-4 mx-auto col-lg-6 bg-white">
          <div className="row">
            <div className="col">
              <h1 className="mb-4">Product Upload Form</h1>
            </div>
            <div className="col-md-3">
              <img src="./ILS_Logo.png" alt="" height="100" width="100" />
            </div>
          </div>

          <AddManufacturer
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onAdd={(newManufacturer) =>
              setManufacturers([...manufacturers, newManufacturer])
            }
          />

          <button
            className="btn btn-success mb-3"
            onClick={() => setIsModalOpen(true)}
            type="button"
          >
            Add New Manufacturer
          </button>

          <form onSubmit={handleSubmit} className="needs-validation" noValidate>
            <div className="form-group ">
              <label className="pb-1">Manufacturer:</label>
              <Select
                options={manufacturers}
                onChange={(selectedOption) =>
                  setSelectedManufacturer(selectedOption)
                }
                value={selectedManufacturer}
              />
            </div>

            <div className="form-group mb-3">
              <label className="pb-1">Product Name:</label>
              <input
                type="text"
                className="form-control"
                value={productName}
                onChange={(e) => setProductName(e.target.value)}
                required
              />
            </div>

            <div className="form-group mb-3">
              <label className="pb-1">Category:</label>
              <Select
                options={Object.keys(recyclableCodes).map((key) => ({
                  value: key,
                  label: key,
                }))}
                onChange={handleCategoryChange}
                value={category && { value: category, label: category }}
              />
            </div>

            {category && (
              <div className="form-group mb-3">
                <label className="pb-1">Sub-category:</label>
                <Select
                  options={subCategories}
                  onChange={handleSubCategoryChange}
                  value={
                    selectedSubCategory && {
                      value: selectedSubCategory,
                      label: selectedSubCategory,
                    }
                  }
                />
              </div>
            )}

            {selectedSubCategory && (
              <div className="form-group mb-3">
                <label className="pb-1">Material Name:</label>
                <Select
                  options={materials}
                  onChange={handleMaterialChange}
                  value={selectedMaterial}
                  placeholder="Select a material"
                />
              </div>
            )}
            <div className="form-group mb-4 ">
              <label className="mb-1">Upload Images:</label>

              <FilePond
                files={images}
                onupdatefiles={(fileItems) => setImages(fileItems)}
                allowMultiple={true}
                maxFiles={3}
                acceptedFileTypes={["image/*"]}
                name="images"
                labelIdle='Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
              />
            </div>

            <div className="rounded shadow-sm p-3 mb-5 border border-primary rounded d-flex flex-column">
              <button
                className="btn btn-success mb-3"
                type="button"
                onClick={handleCleaningSectionToggle}
              >
                Add Cleaning Instructions
              </button>

              {cleaningSectionToggle && (
                <>
                  <p className="text-center">Please select the data type</p>
                  <ToggleButtonGroup
                    type="radio"
                    name="options"
                    value={cleaningInputType}
                    onChange={handleCleaningChange}
                    className="mb-3"
                  >
                    <ToggleButton id="tbg-radio-1" value={1}>
                      File (doc, pdf, etc.)
                    </ToggleButton>
                    <ToggleButton id="tbg-radio-2" value={2}>
                      Text Instructions
                    </ToggleButton>
                    <ToggleButton id="tbg-radio-3" value={3}>
                      Video (URL)
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {cleaningInputType === 1 && (
                    <FilePond
                      files={cleaningFileInput}
                      onupdatefiles={(fileItems) =>
                        setCleaningFileInput(fileItems)
                      }
                      allowMultiple={false}
                      name="cleaningFiles"
                      labelIdle='Drag & Drop your file or <span class="filepond--label-action">Browse</span>'
                    />
                  )}

                  {cleaningInputType === 2 && (
                    <Form.Control
                      as="textarea"
                      rows="7"
                      maxLength={600}
                      placeholder="Cleaning Instructions Text"
                      value={cleaningText}
                      onChange={(e) => setCleaningText(e.target.value)}
                    />
                  )}
                  {cleaningInputType === 3 && (
                    <Form.Control
                      type="text"
                      placeholder="Video URL"
                      value={cleaningVideoURL}
                      onChange={(e) => setCleaningVideoURL(e.target.value)}
                    />
                  )}
                </>
              )}

              {/* Sorting Instruction Section */}

              <button
                className="btn btn-success mb-3 mt-3"
                type="button"
                onClick={handleSortingSectionToggle}
              >
                Add Sorting Instructions
              </button>

              {sortingSectionToggle && (
                <>
                  <p className="text-center">Please select the data type</p>
                  <ToggleButtonGroup
                    type="radio"
                    name="options"
                    value={sortingInputType}
                    onChange={handleSortingChange}
                    className="mb-3"
                  >
                    <ToggleButton id="tbg-radio-4" value={1}>
                      File (doc, pdf, etc.)
                    </ToggleButton>
                    <ToggleButton id="tbg-radio-5" value={2}>
                      Text Instructions
                    </ToggleButton>
                    <ToggleButton id="tbg-radio-6" value={3}>
                      Video (URL)
                    </ToggleButton>
                  </ToggleButtonGroup>

                  {sortingInputType === 1 && (
                    <FilePond
                      files={sortingFileInput}
                      onupdatefiles={(fileItems) =>
                        setSortingFileInput(fileItems)
                      }
                      allowMultiple={false}
                      name="SorringFiles"
                      labelIdle='Drag & Drop your file or <span class="filepond--label-action">Browse</span>'
                    />
                  )}
                  {sortingInputType === 2 && (
                    <Form.Control
                      as="textarea"
                      rows="7"
                      maxLength={600}
                      placeholder="Sorting Instructions Text"
                      value={sortingText}
                      onChange={(e) => setSortingText(e.target.value)}
                    />
                  )}
                  {sortingInputType === 3 && (
                    <Form.Control
                      type="text"
                      placeholder="Video URL"
                      value={sortingVideoURL}
                      onChange={(e) => setSortingVideoURL(e.target.value)}
                    />
                  )}
                </>
              )}
            </div>

            {/* <ButtonToolbar>
    <ToggleButtonGroup type="radio" name="options" defaultValue={1}>
      <ToggleButton value={1}>Radio 1 (pre-checked)</ToggleButton>
      <ToggleButton value={2}>Radio 2</ToggleButton>
      <ToggleButton value={3}>Radio 3</ToggleButton>
    </ToggleButtonGroup>
  </ButtonToolbar> */}

            {/* <div className="btn-group btn-group-toggle" data-toggle="buttons">
      <label className="btn btn-secondary">
        <input type="radio" name="options" id="option1" autoComplete="off" hidden/> Active
      </label>
      <label className="btn btn-secondary">
        <input type="radio" name="options" id="option2" autoComplete="off" hidden/> Radio
      </label>
      <label className="btn btn-secondary">
        <input type="radio" name="options" id="option3" autoComplete="off" hidden/> Radio
      </label>
    </div> */}

            {/* <div className="row">
              <div className="col">
                <button className="btn btn-success mb-3" type="button">
                  Video (URL)
                </button>
              </div>
              <div className="col">
                <button className="btn btn-success mb-3" type="button">
                  Text Instructions
                </button>
              </div>
              <div className="col">
                <button className="btn btn-success mb-3" type="button">
                  File (doc, pdf, etc.)
                </button>
              </div>
            </div> */}

            {/* <div className="form-group mb-3">
              <label className="pb-1">URL</label>
              <Select
                options={materials}
                onChange={handleMaterialChange}
                value={selectedMaterial}
                placeholder="Select a material"
              />
            </div> */}

            <button type="submit" className="btn btn-primary">
              Submit
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}

export default App;
